import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

/**
 * useMagnetic
 * Applies a magnetic hover effect to the element the ref is attached to.
 *
 * @param {React.RefObject} ref - ref to the DOM element (e.g. a button)
 * @param {Object} options
 * @param {boolean} options.enabled  - turn effect on/off (default true)
 * @param {number}  options.strength - how strongly it follows the mouse (default 0.4)
 * @param {number}  options.duration - animation duration in seconds (default 0.8)
 */
export function useMagnetic(ref, { enabled = true, strength = 0.4, duration = 0.8 } = {}) {
  const boundingRect = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return;

    const updateRect = () => {
      boundingRect.current = el.getBoundingClientRect();
    };
    updateRect();

    const handleMouseMove = (e) => {
      if (!boundingRect.current) return;
      const mousePosX = e.clientX - boundingRect.current.left;
      const mousePosY = e.clientY - boundingRect.current.top;
      gsap.to(el, {
        x: (mousePosX - boundingRect.current.width / 2) * strength,
        y: (mousePosY - boundingRect.current.height / 2) * strength,
        duration,
        ease: 'power3.out',
      });
    };

    const handleMouseLeave = () => {
      gsap.to(el, { x: 0, y: 0, duration, ease: 'power2.inOut' });
    };

    window.addEventListener('resize', updateRect);
    el.addEventListener('mousemove', handleMouseMove);
    el.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      window.removeEventListener('resize', updateRect);
      el.removeEventListener('mousemove', handleMouseMove);
      el.removeEventListener('mouseleave', handleMouseLeave);
      gsap.set(el, { x: 0, y: 0 });
    };
  }, [ref, enabled, strength, duration]);
}

/**
 * usePseudoElementAnimate
 *
 * Replicates the ::before and ::after rings of #centerimg as real <span> nodes,
 * then gives each ring a magnetic attraction effect: when the cursor moves near
 * a ring, the ring smoothly pulls toward it; when the cursor leaves the proximity
 * zone the ring elastically snaps back to its resting position.
 *
 * Why real spans?  GSAP cannot target CSS pseudo-elements (they have no DOM node).
 * The injected spans are styled identically to the ::before / ::after rules in
 * BlockDefault.module.css and carry data-pseudo-proxy so cleanup is reliable.
 *
 * Usage in BlockDefault:
 *   usePseudoElementAnimate(blockId === 'centerimg' ? 'centerimg' : null)
 *
 * @param {string|null} id        DOM element id to target (null = disabled)
 * @param {Object}      [options]
 * @param {number} [options.strength=0.35]      how far the ring travels toward cursor (0–1)
 * @param {number} [options.attractRadius=220]  px distance from ring centre that activates pull
 * @param {number} [options.moveDuration=0.6]   GSAP tween duration while attracting
 * @param {number} [options.returnDuration=1.2] GSAP tween duration on snap-back
 * @param {number} [options.fromOpacity=0]      rings start invisible, fade in on scroll-enter
 * @param {string} [options.enterTrigger='top 80%'] ScrollTrigger start for fade-in
 */
export function usePseudoElementAnimate(id, {
  strength = 0.35,
  attractRadius = 220,
  moveDuration = 0.6,
  returnDuration = 1.2,
  fromOpacity = 0,
  enterTrigger = 'top 80%',
} = {}) {
  useEffect(() => {
    if (!id || typeof window === 'undefined') return;

    const DATA_ATTR = 'data-pseudo-proxy';
    let cancelled = false;
    let retryTimeout = null;
    let entryTl = null;
    const cleanupFns = [];

    // ─── Ring geometry (mirrors BlockDefault.module.css #centerimg ::before/::after) ───
    const RING_SIZE = 364;          // px — width & height
    const RING_OFFSET = 52;         // px — distance above/below parent centre

    const sharedStyle = {
      display: 'block',
      position: 'absolute',
      width: `${RING_SIZE}px`,
      height: `${RING_SIZE}px`,
      border: '1px solid #606A76',
      borderRadius: '50%',
      left: '50%',
      marginLeft: `${-(RING_SIZE / 2)}px`,
      zIndex: '1',
      boxSizing: 'border-box',
      willChange: 'transform',
      pointerEvents: 'none',  // hover is detected on the parent, not the ring itself
    };

    // ─── Attach magnetic behaviour to one ring span ───────────────────────────
    const attachMagnetic = (ringSpan, parentEl) => {
      /**
       * We listen on the *parent* (#centerimg) for mousemove so the cursor
       * is always tracked even when it's between the rings.
       * We then check if the cursor is within `attractRadius` of this
       * particular ring's centre, and pull proportionally.
       */
      const getRingCentreInParent = () => {
        const parentRect = parentEl.getBoundingClientRect();
        const ringRect  = ringSpan.getBoundingClientRect();
        return {
          // Centre of the ring relative to the parent's top-left corner
          x: ringRect.left - parentRect.left + ringRect.width  / 2,
          y: ringRect.top  - parentRect.top  + ringRect.height / 2,
        };
      };

      let isAttracted = false;

      const onMouseMove = (e) => {
        const parentRect = parentEl.getBoundingClientRect();
        // Cursor position relative to parent
        const cx = e.clientX - parentRect.left;
        const cy = e.clientY - parentRect.top;

        // Ring centre in parent-local coordinates (ignoring current GSAP offset
        // so we measure from the natural resting position)
        const centre = getRingCentreInParent();

        const dx = cx - centre.x;
        const dy = cy - centre.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist < attractRadius) {
          // Attraction: pull the ring toward cursor, scaled by proximity
          const factor = (1 - dist / attractRadius) * strength;
          gsap.to(ringSpan, {
            x: dx * factor,
            y: dy * factor,
            duration: moveDuration,
            ease: 'power3.out',
            overwrite: 'auto',
          });
          isAttracted = true;
        } else if (isAttracted) {
          // Cursor left the zone — snap back elastically
          gsap.to(ringSpan, {
            x: 0,
            y: 0,
            duration: returnDuration,
            ease: 'power2.inOut',
            overwrite: 'auto',
          });
          isAttracted = false;
        }
      };

      const onMouseLeave = () => {
        if (isAttracted) {
          gsap.to(ringSpan, {
            x: 0,
            y: 0,
            duration: returnDuration,
            ease: 'power2.inOut',
            overwrite: 'auto',
          });
          isAttracted = false;
        }
      };

      parentEl.addEventListener('mousemove', onMouseMove);
      parentEl.addEventListener('mouseleave', onMouseLeave);

      // Return cleanup
      return () => {
        parentEl.removeEventListener('mousemove', onMouseMove);
        parentEl.removeEventListener('mouseleave', onMouseLeave);
        gsap.killTweensOf(ringSpan);
      };
    };

    // ─── Main setup ──────────────────────────────────────────────────────────
    const setup = () => {
      const element = document.getElementById(id);
      if (!element) return false;

      // Remove any leftover proxies from a previous render
      element.querySelectorAll(`[${DATA_ATTR}]`).forEach(n => n.remove());

      // Ensure parent is a positioning context
      const parentPosition = window.getComputedStyle(element).position;
      if (parentPosition === 'static') element.style.position = 'relative';

      // ── Create ::before proxy (ring above) ──
      const beforeProxy = document.createElement('span');
      beforeProxy.setAttribute(DATA_ATTR, 'before');
      Object.assign(beforeProxy.style, sharedStyle, { top: `-${RING_OFFSET}px` });
      element.appendChild(beforeProxy);

      // ── Create ::after proxy (ring below) ──
      const afterProxy = document.createElement('span');
      afterProxy.setAttribute(DATA_ATTR, 'after');
      Object.assign(afterProxy.style, sharedStyle, {
        top: 'auto',
        bottom: `-${RING_OFFSET}px`,
      });
      element.appendChild(afterProxy);

      // ── Fade rings in as the section scrolls into view ──
      gsap.set([beforeProxy, afterProxy], { opacity: fromOpacity });

      entryTl = gsap.timeline({
        scrollTrigger: {
          trigger: element,
          start: enterTrigger,
          once: true,
        },
      });
      entryTl.to(beforeProxy, { opacity: 1, duration: 0.8, ease: 'power2.out' }, 0);
      entryTl.to(afterProxy,  { opacity: 1, duration: 0.8, ease: 'power2.out' }, 0.15);

      // ── Attach magnetic hover to each ring ──
      cleanupFns.push(attachMagnetic(beforeProxy, element));
      cleanupFns.push(attachMagnetic(afterProxy,  element));

      ScrollTrigger.refresh();
      return true;
    };

    if (!setup()) {
      retryTimeout = window.setTimeout(() => {
        if (!cancelled) setup();
      }, 0);
    }

    return () => {
      cancelled = true;
      if (retryTimeout) window.clearTimeout(retryTimeout);

      // Run all magnetic event-listener cleanups
      cleanupFns.forEach(fn => fn());

      entryTl?.scrollTrigger?.kill();
      entryTl?.kill();

      // Remove injected spans
      const element = document.getElementById(id);
      if (element) {
        element.querySelectorAll(`[${DATA_ATTR}]`).forEach(n => n.remove());
      }
    };
  }, [id, strength, attractRadius, moveDuration, returnDuration, fromOpacity, enterTrigger]);
}

/**
 * useCenterImgHover
 *
 * Applies a gentle parallax-style hover movement to the #centerimg element.
 * The element follows the cursor within its bounds and snaps back elastically
 * when the cursor leaves.
 *
 * @param {string|null} id          DOM element id (null = disabled)
 * @param {Object}      [options]
 * @param {number} [options.strength=0.12]       how far the element travels (0–1)
 * @param {number} [options.moveDuration=0.8]    tween duration while following cursor
 * @param {number} [options.returnDuration=1.2]  snap-back duration
 * @param {string} [options.ease='power3.out']
 */
export function useCenterImgHover(id, {
  strength = 0.12,
  moveDuration = 0.8,
  returnDuration = 1.2,
  ease = 'power3.out',
} = {}) {
  useEffect(() => {
    if (!id || typeof window === 'undefined') return;

    let cancelled = false;
    let observer = null;
    let cleanup = null;

    const setup = () => {
      const element = document.getElementById(id);
      if (!element) return false;

      // Use simple querySelector('a') — there is exactly one <a> inside #centerimg
      const anchor = element.querySelector('a');
      if (!anchor) return false;

      const onMouseMove = (e) => {
        const rect = element.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const dx = e.clientX - cx;
        const dy = e.clientY - cy;

        gsap.to(anchor, {
          x: dx * strength,
          y: dy * strength,
          duration: moveDuration,
          ease,
          overwrite: 'auto',
        });
      };

      const onMouseLeave = () => {
        gsap.to(anchor, {
          x: 0,
          y: 0,
          duration: returnDuration,
          ease: 'elastic.out(1, 0.4)',
          overwrite: 'auto',
        });
      };

      // Listen on the container — tracks cursor across the whole area
      element.addEventListener('mousemove', onMouseMove);
      element.addEventListener('mouseleave', onMouseLeave);

      cleanup = () => {
        element.removeEventListener('mousemove', onMouseMove);
        element.removeEventListener('mouseleave', onMouseLeave);
        gsap.killTweensOf(anchor);
        gsap.set(anchor, { x: 0, y: 0, clearProps: 'transform' });
      };

      return true;
    };

    const tryWithDelay = () => {
      // Small delay so Sharetribe's hydration finishes rendering the <a>
      window.setTimeout(() => {
        if (!cancelled) setup();
      }, 100);
    };

    if (!setup()) {
      // Element or anchor not in DOM yet — watch for it
      observer = new MutationObserver(() => {
        if (cancelled) return;
        if (setup()) {
          observer.disconnect();
          observer = null;
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });

      // Also try after a short delay as a safety net
      tryWithDelay();
    }

    return () => {
      cancelled = true;
      observer?.disconnect();
      cleanup?.();
    };
  }, [id, strength, moveDuration, returnDuration, ease]);
}


/**
 * useMagneticCta
 *
 * Two-element interactive magnetic effect:
 *   - `btnRef`     (the filled CTA button)  → ATTRACTS toward cursor
 *   - `ringRef`    (the outer outline ring)  → REPELS away from cursor (opposite direction)
 *
 * Both elements snap back elastically when the cursor leaves the zone.
 * Interaction zone is detected on `wrapperRef`.
 *
 * @param {React.RefObject} wrapperRef  - the container that captures mouse events
 * @param {React.RefObject} btnRef      - the CTA button (magnetic attract)
 * @param {React.RefObject} ringRef     - the outline ring (magnetic repel)
 * @param {Object}  [options]
 * @param {boolean} [options.enabled=true]
 * @param {number}  [options.btnStrength=0.45]    attraction pull (0–1)
 * @param {number}  [options.ringStrength=0.2]    repulsion push (0–1)
 * @param {number}  [options.moveDuration=0.5]    tween duration while moving
 * @param {number}  [options.returnDuration=1.0]  elastic snap-back duration
 */
export function useMagneticCta(wrapperRef, btnRef, ringRef, {
  enabled = true,
  btnStrength = 0.12,
  ringStrength = 0.06,
  moveDuration = 1.0,
  returnDuration = 1.6,
} = {}) {
  useEffect(() => {
    const wrapper = wrapperRef.current;
    const btn     = btnRef.current;
    const ring    = ringRef.current;

    if (!wrapper || !btn || !enabled) return;

    let rafId = null;
    let cleanup = () => {};

    const getWrapperCentre = () => {
      const r = wrapper.getBoundingClientRect();
      return { cx: r.left + r.width / 2, cy: r.top + r.height / 2 };
    };

    const handleMouseMove = (e) => {
      const { cx, cy } = getWrapperCentre();
      const dx = e.clientX - cx;
      const dy = e.clientY - cy;

      gsap.to(btn, {
        x: dx * btnStrength,
        y: dy * btnStrength,
        duration: moveDuration,
        ease: 'power1.out',
        overwrite: 'auto',
      });

      if (ring) {
        gsap.to(ring, {
          x: -dx * ringStrength,
          y: -dy * ringStrength,
          duration: moveDuration * 1.4,
          ease: 'power1.out',
          overwrite: 'auto',
        });
      }
    };

    const handleMouseLeave = () => {
      gsap.to(btn, {
        x: 0, y: 0,
        duration: returnDuration,
        ease: 'elastic.out(1, 0.4)',
        overwrite: 'auto',
      });
      if (ring) {
        gsap.to(ring, {
          x: 0, y: 0,
          duration: returnDuration * 0.8,
          ease: 'elastic.out(1, 0.5)',
          overwrite: 'auto',
        });
      }
    };

    // Double rAF: first frame ends JS execution, second frame
    // guarantees the browser has painted and laid out the elements.
    // This is the only reliable way to get correct getBoundingClientRect
    // values after a client-side navigation.
    rafId = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!wrapperRef.current) return; // unmounted before paint

        gsap.set([btn, ring].filter(Boolean), { x: 0, y: 0, clearProps: 'transform' });

        wrapper.addEventListener('mousemove', handleMouseMove);
        wrapper.addEventListener('mouseleave', handleMouseLeave);

        cleanup = () => {
          wrapper.removeEventListener('mousemove', handleMouseMove);
          wrapper.removeEventListener('mouseleave', handleMouseLeave);
          gsap.killTweensOf([btn, ring].filter(Boolean));
          gsap.set([btn, ring].filter(Boolean), { x: 0, y: 0, clearProps: 'transform' });
        };
      });
    });

    return () => {
      cancelAnimationFrame(rafId);
      cleanup();
    };
  }, [wrapperRef, btnRef, ringRef, enabled, btnStrength, ringStrength, moveDuration, returnDuration]);
}
